import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Car, Loader2 } from 'lucide-react';
import { ROUTES } from '../constants/routes.constants';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import VehicleCard from '../components/vehicule/VehicleCard';
import vehicleService from '../services/vehicleService';
import { formatVehicleForCard } from '../utils/vehicleUtils';

const VehiclesPage = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const city = searchParams.get('city');
  const start = searchParams.get('start');
  const end = searchParams.get('end');
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await vehicleService.getAllVehicles({
          city: city || undefined,
          startDate: start || undefined,
          endDate: end || undefined,
          isActive: true,
          isVerified: true,
          limit: 100,
          sortBy: 'rating.average',
          sortOrder: 'desc',
        });

        const vehiclesArray = response?.data?.vehicles || response?.data || [];
        const formattedVehicles = (Array.isArray(vehiclesArray) ? vehiclesArray : [])
          .map((vehicle) => formatVehicleForCard(vehicle))
          .filter((vehicle) => vehicle !== null);

        setVehicles(formattedVehicles);
      } catch (err) {
        console.error('Erreur lors du chargement des vehicules:', err);
        setError(err?.message || 'Erreur lors du chargement des vehicules.');
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, [city, start, end]);

  return (
    <div className="container mx-auto px-4 py-12">
      <Button variant="ghost" onClick={() => navigate(ROUTES.HOME)} className="mb-4 pl-0">
        ← {t('common.back')}
      </Button>
      <h1 className="text-3xl font-bold mb-4">
        {city ? `Résultats pour ${city}` : t('vehicles.viewAllCatalog')}
      </h1>
      {(city || start || end) && (
        <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          <p><span className="font-semibold">Ville :</span> {city || '-'}</p>
          <p><span className="font-semibold">Départ :</span> {start || '-'}</p>
          <p><span className="font-semibold">Retour :</span> {end || '-'}</p>
        </div>
      )}

      <h2 className="text-xl font-semibold text-gray-800 mb-6">
        Vehicules disponibles a {city || 'toutes les destinations'}
      </h2>

      {loading && (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-10 h-10 text-[#27489E] animate-spin mb-4" />
          <p className="text-gray-500">Recherche des vehicules en cours...</p>
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-2 mb-8">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && vehicles.length === 0 && (
        <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-200">
          <Car className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">
            Aucun vehicule trouve pour ces dates et ce lieu.
          </h3>
          <p className="text-gray-500 mb-6">Essaie de modifier la ville ou la periode.</p>
          <Button onClick={() => navigate(-1)}>Modifier la recherche</Button>
        </div>
      )}

      {!loading && !error && vehicles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {vehicles.map((vehicle) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              isLoggedIn={isAuthenticated}
              onLoginClick={() => navigate(ROUTES.LOGIN)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VehiclesPage;

